/**
 * Small geodesy helpers shared by the map engine, data scripts and UI.
 * Owned by the main loop. Erasable TS only (Node scripts import it).
 * All coordinates are [lng, lat].
 */
export type LngLat = [number, number];

const R = 6371008.8; // mean Earth radius, metres
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function haversine(a: LngLat, b: LngLat): number {
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Initial bearing a→b in degrees [0, 360). */
export function bearing(a: LngLat, b: LngLat): number {
  const φ1 = toRad(a[1]), φ2 = toRad(b[1]), Δλ = toRad(b[0] - a[0]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** Destination point from `a`, travelling `m` metres on bearing `brg` degrees. */
export function destination(a: LngLat, m: number, brg: number): LngLat {
  const δ = m / R, θ = toRad(brg), φ1 = toRad(a[1]), λ1 = toRad(a[0]);
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return [((toDeg(λ2) + 540) % 360) - 180, toDeg(φ2)];
}

/** Geodesic circle as a closed ring (first == last). */
export function circle(center: LngLat, radiusM: number, steps = 256): LngLat[] {
  const out: LngLat[] = [];
  for (let i = 0; i <= steps; i++) out.push(destination(center, radiusM, (i / steps) * 360));
  return out;
}

/** Cumulative metres along a polyline (cum[0] = 0). */
export function cumulative(coords: LngLat[]): number[] {
  const cum = new Array<number>(coords.length);
  cum[0] = 0;
  for (let i = 1; i < coords.length; i++) cum[i] = cum[i - 1] + haversine(coords[i - 1], coords[i]);
  return cum;
}

/** Point + bearing at `m` metres along a polyline with precomputed `cum` (binary search). */
export function along(coords: LngLat[], cum: number[], m: number): { lngLat: LngLat; bearing: number; index: number } {
  const n = coords.length;
  if (m <= 0) return { lngLat: coords[0], bearing: bearing(coords[0], coords[1]), index: 0 };
  if (m >= cum[n - 1]) return { lngLat: coords[n - 1], bearing: bearing(coords[n - 2], coords[n - 1]), index: n - 2 };
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= m) lo = mid;
    else hi = mid;
  }
  const seg = cum[hi] - cum[lo] || 1;
  const t = (m - cum[lo]) / seg;
  const a = coords[lo], b = coords[hi];
  return { lngLat: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], bearing: bearing(a, b), index: lo };
}

/** Bounding box [w, s, e, n] of coordinates. */
export function bbox(coords: LngLat[]): [number, number, number, number] {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  for (const [x, y] of coords) {
    if (x < w) w = x;
    if (y < s) s = y;
    if (x > e) e = x;
    if (y > n) n = y;
  }
  return [w, s, e, n];
}

/** Web-Mercator (MapLibre MercatorCoordinate units, 0..1 world). */
export function mercator([lng, lat]: LngLat): [number, number] {
  const x = (180 + lng) / 360;
  const y = (180 - toDeg(Math.log(Math.tan(Math.PI / 4 + toRad(lat) / 2)))) / 360;
  return [x, y];
}

/** 8-point compass word for a bearing ("North-east"). */
export function compass(brg: number): string {
  const names = ["North", "North-east", "East", "South-east", "South", "South-west", "West", "North-west"];
  return names[Math.round(((brg % 360) + 360) % 360 / 45) % 8];
}

/** Shortest signed angular difference b - a in degrees (-180, 180]. */
export function angleDelta(a: number, b: number): number {
  let d = ((b - a) % 360 + 540) % 360 - 180;
  if (d === -180) d = 180;
  return d;
}

export const clamp = (v: number, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
